import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'motion/react'
import { Mail, MapPin, NotebookPen, Send, X } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/lib/auth/useAuth'
import { emailExists } from '@/lib/auth/auth'
import { useAuthUi } from '@/lib/auth/authUi'

type Status = 'idle' | 'sending' | 'sent' | 'error'

const GMAPS_RE =
  /^https?:\/\/(maps\.app\.goo\.gl|maps\.google\.[a-z.]+|www\.google\.[a-z.]+\/maps|goo\.gl|maps\.apple\.com)\//i

export default function SuggestRoad() {
  const { session, profile } = useAuth()
  const openAuthModal = useAuthUi((s) => s.openAuthModal)
  const suggestReopenSeq = useAuthUi((s) => s.suggestReopenSeq)
  const lockedEmail = Boolean(session && profile?.email)
  const [open, setOpen] = useState(false)
  // When a sign-in that started from this panel's login prompt succeeds, the
  // AuthModal bumps suggestReopenSeq — bring the panel back up, and since the
  // user is now signed in the stale email-match prompt is dropped.
  useEffect(() => {
    if (suggestReopenSeq > 0) {
      setOpen(true)
      setEmailMatch(null)
    }
  }, [suggestReopenSeq])
  // Re-evaluate the email-match prompt every time the panel is shown, so it's
  // always accurate (e.g. the user logged in elsewhere in the meantime).
  useEffect(() => {
    if (open) setEmailMatch(null)
  }, [open])
  const [url, setUrl] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errMsg, setErrMsg] = useState('')
  const [emailMatch, setEmailMatch] = useState<null | { matched: boolean; checked: boolean }>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // Render the popup into the document body so ancestor transforms/filters
  // on the info panel can't turn `fixed` positioning into panel-relative.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // While logged out, check the typed email against existing accounts
  // (debounced) so we can offer to link the suggestion to their stats.
  useEffect(() => {
    if (lockedEmail || !open || status === 'sending') return
    const addr = email.trim()
    if (!addr || !addr.includes('@') || /\s/.test(addr)) {
      setEmailMatch(null)
      return
    }
    let cancelled = false
    const t = setTimeout(async () => {
      const exists = await emailExists(addr)
      if (!cancelled) setEmailMatch({ matched: exists === true, checked: exists !== null })
    }, 600)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [email, lockedEmail, open, status])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'sending') return
    const link = url.trim()
    if (!link || !GMAPS_RE.test(link)) {
      setStatus('error')
      setErrMsg('Please paste a valid Google Maps link.')
      return
    }
    setStatus('sending')
    setErrMsg('')
    const { error } = await supabase.functions.invoke('road-suggestion', {
      body: {
        googleMapsUrl: link,
        email: (lockedEmail ? (profile?.email ?? '') : email).trim(),
        notes: notes.trim(),
        nickname: '',
      },
    })
    if (error) {
      setStatus('error')
      setErrMsg('Could not send your suggestion — please try again.')
      return
    }
    // Record the suggestion for per-user stats + the admin moderation queue.
    // Fire-and-forget: the email already went out, so a DB hiccup here must
    // not flip the success UI into an error.
    try {
      await supabase.from('road_suggestions').insert({
        user_id: session?.user.id ?? null,
        email: (lockedEmail ? profile?.email : email.trim()) || null,
        google_maps_url: link,
        notes: notes.trim() || null,
      })
    } catch {
      /* ignore */
    }
    setStatus('sent')
    setUrl('')
    if (!lockedEmail) setEmail('')
    setNotes('')
  }

  const close = () => {
    setOpen(false)
    if (status !== 'sending') setStatus('idle')
  }

  const modal = (
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              ref={panelRef}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-[420px] max-h-[calc(100vh-32px)] overflow-y-auto"
            >
            <div className="rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-white/10 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-mono uppercase tracking-widest text-amber-400">
                    Suggest a road
                  </div>
                  <div className="text-base text-white/80 mt-1 leading-snug">
                    Know a great riding road we're missing? Paste its Google Maps link and we'll check it out.
                  </div>
                </div>
                <button
                  onClick={close}
                  className="h-9 w-9 -mr-1 -mt-1 flex items-center justify-center rounded-lg text-white/50 hover:text-white"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              {status === 'sent' ? (
                <div className="py-2 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-emerald-300">
                    <Send size={15} className="text-emerald-400" />
                    Thanks! Your suggestion is on its way.
                  </div>
                  <button
                    onClick={close}
                    className="min-h-11 w-full rounded-lg bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300"
                  >
                    DONE
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-2.5">
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
                    <input
                      type="url"
                      inputMode="url"
                      required
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="Paste Google Maps link"
                      className="min-h-11 w-full rounded-lg bg-white/[0.05] border border-white/10 pl-9 pr-3 text-sm text-white placeholder:text-white/55 focus:outline-none focus:border-amber-400/60"
                    />
                  </div>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
                    <input
                      type="email"
                      value={lockedEmail ? (profile?.email ?? '') : email}
                      onChange={(e) => setEmail(e.target.value)}
                      readOnly={lockedEmail}
                      placeholder={lockedEmail ? undefined : 'Your email (optional)'}
                      title={lockedEmail ? 'Signed in — using your account email' : undefined}
                      className={
                        'min-h-11 w-full rounded-lg bg-white/[0.05] border border-white/10 pl-9 pr-3 text-sm text-white placeholder:text-white/55 focus:outline-none' +
                        (lockedEmail
                          ? ' opacity-60 cursor-not-allowed'
                          : ' focus:border-amber-400/60')
                      }
                    />
                  </div>

                  {emailMatch?.matched && (
                    <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 px-3 py-2.5 space-y-2">
                      <div className="text-xs text-amber-200 leading-snug">
                        This email belongs to a RoadRatings account. Log in so this suggestion counts toward your stats?
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false)
                          openAuthModal('suggestion')
                        }}
                        className="min-h-9 w-full rounded-lg bg-amber-400 text-black text-xs font-semibold hover:bg-amber-300"
                      >
                        LOG IN
                      </button>
                    </div>
                  )}

                  <div className="relative">
                    <NotebookPen size={16} className="absolute left-3 top-3.5 text-white/60" />
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Anything we should know about the road? Is it fully paved? Cool features? etc."
                      className="w-full rounded-lg bg-white/[0.05] border border-white/10 pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-white/55 focus:outline-none focus:border-amber-400/60 resize-none"
                    />
                  </div>

                  {status === 'error' && (
                    <div className="text-xs text-red-300">{errMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || !GMAPS_RE.test(url.trim())}
                    className="min-h-11 w-full rounded-lg bg-amber-400 text-black text-sm font-semibold hover:bg-amber-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status === 'sending' ? (
                      'SENDING…'
                    ) : (
                      <>
                        <Send size={14} className="shrink-0" /> SEND SUGGESTION
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
  )

  return (
    <>
      {mounted && createPortal(modal, document.body)}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full min-h-11 h-11 px-4 rounded-lg bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold flex items-center justify-center gap-2"
      >
        <Send size={14} className="shrink-0" />
        Suggest a road
      </button>
    </>
  )
}
